import React, { useState, useEffect } from 'react';
import apiClient from '../api/api';
import { FaCheckCircle, FaTimesCircle, FaIdCard, FaUser, FaSearch, FaEye } from 'react-icons/fa';
import loadImage from '../assets/imageLoader';

const AdminVerificationsPage = () => {
    const [pendingUsers, setPendingUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null); // Lightbox

    const fetchPending = async () => {
        try {
            setLoading(true);
            const res = await apiClient.get('/admin/pending-verifications');
            setPendingUsers(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPending();
    }, []);

    const handleAction = async (userId, approve) => {
        const confirmMsg = approve 
            ? "Approve this user's identity? They will be marked as Verified."
            : "Reject this verification? Note: This won't delete the user, just keeps them unverified.";
        
        if (!window.confirm(confirmMsg)) return;

        try {
            if (approve) {
                await apiClient.put(`/users/${userId}`, { is_Verified: true });
                setPendingUsers(pendingUsers.filter(u => u.id !== userId));
            } else {
                // For rejection, we could clear the id_document_url or just notify.
                // For now, let's just keep them in pending or maybe add a status?
                // Minimal: just mark as unverified (already are) and maybe clear doc.
                alert("Rejection logic could clear the document URL or notify the user. Currently just closing.");
            }
        } catch (err) {
            alert("Action failed.");
        }
    };

    if (loading) return <div className="p-10 text-center font-bold text-slate-500">Loading verification queue...</div>;

    return (
        <div className="space-y-8 font-sans">
            {/* Lightbox */}
            {selectedImage && (
                <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-10 cursor-zoom-out" onClick={() => setSelectedImage(null)}>
                    <img src={selectedImage} className="max-w-full max-h-full rounded-lg shadow-2xl" alt="Large View" />
                    <button className="absolute top-10 right-10 text-white text-4xl hover:text-red-500 transition-colors">
                        <FaTimesCircle />
                    </button>
                </div>
            )}

            <div>
                <h1 className="text-3xl font-black text-slate-900 tracking-tight">Identity Verifications</h1>
                <p className="text-slate-500 mt-1 font-medium">Review NID/Passport uploads and approve user credentials.</p>
            </div>

            {pendingUsers.length === 0 ? (
                <div className="bg-white rounded-[2rem] border-2 border-dashed border-slate-200 py-32 text-center">
                    <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-emerald-500 shadow-inner">
                        <FaCheckCircle size={30} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800">Inbox Zero!</h3>
                    <p className="text-slate-400 font-medium">All pending verification requests have been processed.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    {pendingUsers.map(user => (
                        <div key={user.id} className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden flex flex-col md:flex-row group hover:border-emerald-200 transition-all">
                            
                            {/* Document Preview */}
                            <div className="md:w-64 bg-slate-100 relative overflow-hidden flex-shrink-0">
                                <img 
                                    src={loadImage(user.profile?.id_document_url)} 
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 cursor-zoom-in"
                                    alt="ID Document"
                                    onClick={() => setSelectedImage(loadImage(user.profile?.id_document_url))}
                                />
                                <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                                    <FaEye className="text-white text-2xl" />
                                </div>
                                <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-2 py-1 rounded text-[10px] font-black uppercase text-slate-600 shadow-sm">
                                    {user.profile?.id_type || 'ID Document'}
                                </div>
                            </div>

                            {/* User Info & Actions */}
                            <div className="p-8 flex-grow flex flex-col">
                                <div className="mb-6">
                                    <h3 className="text-xl font-black text-slate-800 mb-1">{user.profile?.name || user.username}</h3>
                                    <p className="text-xs font-bold text-slate-400 mb-4 tracking-widest">@{user.username}</p>
                                    
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2 text-sm text-slate-600">
                                            <FaIdCard className="text-slate-300" />
                                            <span className="font-bold">ID: {user.profile?.id_number || 'N/A'}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-slate-600">
                                            <FaUser className="text-slate-300" />
                                            <span className="font-medium">{user.email}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-auto pt-6 border-t border-slate-50 flex gap-3">
                                    <button 
                                        onClick={() => handleAction(user.id, true)}
                                        className="flex-1 bg-emerald-600 text-white py-3 rounded-xl font-black text-sm shadow-lg shadow-emerald-200 hover:bg-emerald-700 transition-all active:scale-95"
                                    >
                                        Approve
                                    </button>
                                    <button 
                                        onClick={() => handleAction(user.id, false)}
                                        className="px-6 py-3 bg-slate-100 text-slate-500 rounded-xl font-bold text-sm hover:bg-red-50 hover:text-red-500 transition-all"
                                    >
                                        Reject
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AdminVerificationsPage;